import React, { useState, useEffect, useCallback } from 'react';
import { framesApi } from '../api';
import StatusBadge from '../shared/StatusBadge';
import ConfirmDialog from '../shared/ConfirmDialog';
import JsonViewer from '../shared/JsonViewer';

const EMPTY_RULE = {
  workspace_id: '',
  entity_type: '',
  session_key_template: '',
  close_conditions: { max_duration_ms: 30000, max_events: 50, semantic_completeness: '', explicit_trigger: '' },
};

export default function FramesTab({ workspaceId }) {
  const [frames, setFrames] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedFrame, setExpandedFrame] = useState(null);
  const [frameDetail, setFrameDetail] = useState(null);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [deleteRuleId, setDeleteRuleId] = useState(null);
  const [ruleForm, setRuleForm] = useState({ ...EMPTY_RULE, workspace_id: workspaceId });

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [f, r] = await Promise.all([
        framesApi.list(workspaceId),
        framesApi.listRules(workspaceId),
      ]);
      setFrames(Array.isArray(f) ? f : []);
      setRules(Array.isArray(r) ? r : []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const handleClose = async (frameId) => {
    try {
      const r = await framesApi.close(frameId);
      alert(`Frame closed. Composite narrative:\n${r.composite_narrative?.slice(0, 300) || '(none)'}...`);
      load();
    } catch (err) { alert('Close error: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleExpand = async (frameId) => {
    if (expandedFrame === frameId) { setExpandedFrame(null); return; }
    try {
      const d = await framesApi.get(frameId);
      setFrameDetail(d);
      setExpandedFrame(frameId);
    } catch (err) { alert('Error loading frame detail'); }
  };

  const handleEditRule = (r) => {
    setRuleForm({
      workspace_id: workspaceId,
      entity_type: r.entity_type || '',
      session_key_template: r.session_key_template || '',
      close_conditions: {
        max_duration_ms: r.close_conditions?.max_duration_ms ?? 30000,
        max_events: r.close_conditions?.max_events ?? 50,
        semantic_completeness: r.close_conditions?.semantic_completeness || '',
        explicit_trigger: r.close_conditions?.explicit_trigger || '',
      },
    });
    setEditingRuleId(r.rule_id);
    setShowRuleForm(true);
  };

  const handleDeleteRule = async () => {
    if (!deleteRuleId) return;
    try {
      await framesApi.deleteRule(deleteRuleId);
      setDeleteRuleId(null);
      load();
    } catch (err) {
      alert('Delete error: ' + (err.response?.data?.detail || err.message));
      setDeleteRuleId(null);
    }
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...ruleForm,
        workspace_id: workspaceId,
        close_conditions: {
          max_duration_ms: parseInt(ruleForm.close_conditions.max_duration_ms) || 30000,
          max_events: parseInt(ruleForm.close_conditions.max_events) || 50,
          semantic_completeness: ruleForm.close_conditions.semantic_completeness || null,
          explicit_trigger: ruleForm.close_conditions.explicit_trigger || null,
        },
      };
      if (editingRuleId) {
        await framesApi.updateRule(editingRuleId, payload);
      } else {
        await framesApi.createRule(payload);
      }
      setShowRuleForm(false);
      setEditingRuleId(null);
      setRuleForm({ ...EMPTY_RULE, workspace_id: workspaceId });
      load();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  const setRule = (k, v) => setRuleForm(prev => ({ ...prev, [k]: v }));
  const setClose = (k, v) => setRuleForm(prev => ({ ...prev, close_conditions: { ...prev.close_conditions, [k]: v } }));

  return (
    <div>
      {/* Active Frames */}
      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">Active Transaction Frames</h2>
          <button className="ednl-btn ednl-btn--sm" onClick={load}>Refresh</button>
        </div>

        {loading ? <div className="ednl-loading">Loading...</div> : frames.length === 0 ? (
          <div className="ednl-empty"><div className="ednl-empty__icon">📦</div><div className="ednl-empty__text">No active frames</div></div>
        ) : (
          <div className="ednl-table-wrap">
            <table className="ednl-table">
              <thead><tr><th>Session Key</th><th>Entity</th><th>Events</th><th>Status</th><th>Opened</th><th>Last Activity</th><th>Actions</th></tr></thead>
              <tbody>
                {frames.map(f => (
                  <React.Fragment key={f.frame_id}>
                    <tr>
                      <td className="mono" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.session_key}</td>
                      <td>{f.entity_type} {f.entity_id}</td>
                      <td>{f.event_count}</td>
                      <td><StatusBadge status={f.status} /></td>
                      <td>{f.opened_at?.slice(11, 19)}</td>
                      <td>{f.last_activity?.slice(11, 19)}</td>
                      <td>
                        <div className="ednl-btn-group">
                          <button className="ednl-btn ednl-btn--sm" onClick={() => handleExpand(f.frame_id)}>
                            {expandedFrame === f.frame_id ? 'Hide' : 'Detail'}
                          </button>
                          <button className="ednl-btn ednl-btn--sm ednl-btn--danger" onClick={() => handleClose(f.frame_id)}>Close</button>
                        </div>
                      </td>
                    </tr>
                    {expandedFrame === f.frame_id && frameDetail && (
                      <tr className="ednl-expand-row">
                        <td colSpan={7}>
                          <h4 style={{ marginBottom: 8 }}>Narratives ({frameDetail.narratives?.length || 0})</h4>
                          {(frameDetail.narratives || []).map((n, i) => (
                            <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #eee', fontSize: 12 }}>
                              <span style={{ color: '#6b7280', marginRight: 8 }}>{n.timestamp?.slice(11, 19)}</span>
                              <StatusBadge status="info" label={n.narrative_type} />
                              <span style={{ marginLeft: 8 }}>{n.narrative_text}</span>
                            </div>
                          ))}
                          {frameDetail.composite_narrative && (
                            <>
                              <h4 style={{ margin: '12px 0 8px' }}>Composite Narrative</h4>
                              <div style={{ background: '#f9fafb', padding: 12, borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap' }}>{frameDetail.composite_narrative}</div>
                            </>
                          )}
                          <JsonViewer data={frameDetail} label="Full frame JSON" />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Frame Rules */}
      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">Frame Accumulation Rules</h2>
          <button className="ednl-btn ednl-btn--primary" onClick={() => { setShowRuleForm(!showRuleForm); if (showRuleForm) { setEditingRuleId(null); setRuleForm({ ...EMPTY_RULE, workspace_id: workspaceId }); } }}>
            {showRuleForm ? 'Cancel' : '+ New Rule'}
          </button>
        </div>

        {showRuleForm && (
          <form className="ednl-form" onSubmit={handleCreateRule} style={{ marginBottom: 20 }}>
            <div className="ednl-form-row">
              <div className="ednl-form-group"><label className="ednl-label">Entity Type</label><input className="ednl-input" value={ruleForm.entity_type} onChange={e => setRule('entity_type', e.target.value)} required /></div>
              <div className="ednl-form-group"><label className="ednl-label">Session Key Template (Jinja2)</label><input className="ednl-input" value={ruleForm.session_key_template} onChange={e => setRule('session_key_template', e.target.value)} required placeholder="patient:{{ row.pt_id }}:visit:{{ row.visit_id }}" /></div>
            </div>
            <div className="ednl-form-row">
              <div className="ednl-form-group"><label className="ednl-label">Max Duration (ms)</label><input className="ednl-input" type="number" value={ruleForm.close_conditions.max_duration_ms} onChange={e => setClose('max_duration_ms', e.target.value)} /></div>
              <div className="ednl-form-group"><label className="ednl-label">Max Events</label><input className="ednl-input" type="number" value={ruleForm.close_conditions.max_events} onChange={e => setClose('max_events', e.target.value)} /></div>
              <div className="ednl-form-group"><label className="ednl-label">Semantic Completeness</label><input className="ednl-input" value={ruleForm.close_conditions.semantic_completeness} onChange={e => setClose('semantic_completeness', e.target.value)} placeholder="discharge_recorded" /></div>
            </div>
            <div><button className="ednl-btn ednl-btn--primary" type="submit">{editingRuleId ? 'Save Changes' : 'Create Rule'}</button></div>
          </form>
        )}

        {rules.length === 0 ? (
          <div className="ednl-empty"><div className="ednl-empty__text">No rules configured</div></div>
        ) : (
          <div className="ednl-table-wrap">
            <table className="ednl-table">
              <thead><tr><th>Entity Type</th><th>Session Key Template</th><th>Max Duration</th><th>Max Events</th><th>Semantic</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>
                {rules.map(r => (
                  <tr key={r.rule_id}>
                    <td>{r.entity_type}</td>
                    <td className="mono">{r.session_key_template}</td>
                    <td>{r.close_conditions?.max_duration_ms}ms</td>
                    <td>{r.close_conditions?.max_events}</td>
                    <td>{r.close_conditions?.semantic_completeness || '—'}</td>
                    <td><StatusBadge status={r.is_active ? 'active' : 'inactive'} /></td>
                    <td>
                      <div className="ednl-btn-group">
                        <button className="ednl-btn ednl-btn--sm" onClick={() => handleEditRule(r)}>Edit</button>
                        <button className="ednl-btn ednl-btn--sm ednl-btn--danger" onClick={() => setDeleteRuleId(r.rule_id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog open={!!deleteRuleId} title="Delete Rule" message="Permanently delete this frame accumulation rule?" onConfirm={handleDeleteRule} onCancel={() => setDeleteRuleId(null)} />
    </div>
  );
}
